import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import {
  AlertTriangle,
  Check,
  CheckCircle2,
  Info,
  Trash2,
  X,
} from 'lucide-react';
import '../../styles/components/confirm.css';

const ConfirmContext = createContext(null);

const defaultConfirmOptions = {
  title: 'Are you sure?',
  message: 'This action cannot be undone.',
  itemName: '',
  warning: '',
  confirmText: 'Confirm',
  cancelText: 'Cancel',
  closeText: 'Close',
  variant: 'danger',
};

const confirmIcons = {
  danger: Trash2,
  warning: AlertTriangle,
  success: CheckCircle2,
  info: Info,
};

export function ConfirmProvider({ children }) {
  const [dialog, setDialog] = useState(null);
  const resolverRef = useRef(null);

  const confirm = useCallback((options = {}) => {
    return new Promise((resolve) => {
      resolverRef.current = resolve;

      setDialog({
        ...defaultConfirmOptions,
        ...options,
      });
    });
  }, []);

  const closeDialog = useCallback((result) => {
    if (resolverRef.current) {
      resolverRef.current(Boolean(result));
      resolverRef.current = null;
    }

    setDialog(null);
  }, []);

  useEffect(() => {
    if (!dialog) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        closeDialog(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [dialog, closeDialog]);

  const contextValue = useMemo(
    () => ({
      confirm,
    }),
    [confirm],
  );

  const Icon = dialog
    ? confirmIcons[dialog.variant] || AlertTriangle
    : AlertTriangle;

  return (
    <ConfirmContext.Provider value={contextValue}>
      {children}

      {dialog &&
        createPortal(
          <div
            className="confirm-backdrop"
            role="presentation"
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) {
                closeDialog(false);
              }
            }}
          >
            <section
              className={`confirm-modal confirm-modal--${dialog.variant}`}
              role="dialog"
              aria-modal="true"
              aria-labelledby="confirm-modal-title"
              aria-describedby="confirm-modal-message"
            >
              <button
                type="button"
                className="confirm-modal__close"
                onClick={() => closeDialog(false)}
                aria-label={dialog.closeText}
              >
                <X size={18} />
              </button>

              <div className="confirm-modal__header">
                <span className="confirm-modal__icon" aria-hidden="true">
                  <Icon size={28} />
                </span>

                <h2 id="confirm-modal-title">{dialog.title}</h2>

                <p id="confirm-modal-message">{dialog.message}</p>

                {dialog.itemName && (
                  <strong className="confirm-modal__item-name">
                    {dialog.itemName}
                  </strong>
                )}
              </div>

              {dialog.warning && (
                <div className="confirm-modal__warning">
                  <AlertTriangle size={18} />
                  <span>{dialog.warning}</span>
                </div>
              )}

              <div className="confirm-modal__actions">
                <button
                  type="button"
                  className="confirm-modal__button confirm-modal__button--cancel"
                  onClick={() => closeDialog(false)}
                >
                  {dialog.cancelText}
                </button>

                <button
                  type="button"
                  className={`confirm-modal__button confirm-modal__button--${dialog.variant}`}
                  onClick={() => closeDialog(true)}
                >
                  <Check size={17} />
                  {dialog.confirmText}
                </button>
              </div>
            </section>
          </div>,
          document.body,
        )}
    </ConfirmContext.Provider>
  );
}

export const useConfirm = () => {
  const context = useContext(ConfirmContext);

  if (!context) {
    throw new Error('useConfirm must be used inside ConfirmProvider');
  }

  return context;
};

export default ConfirmContext;